import React from 'react'
import UserContext from "../dataContext";
import * as XLSX from 'xlsx';

//icons
import { BsPlus, BsDownload } from "react-icons/bs";
import Overlay from "../Components/Subcomponents/Overlay/Overlay";
import Spinner from '../Components/Spinner';
import HoursMatrixExport from '../Components/HoursMatrixExport';
import {
    durationToHours,
    roundHours,
    formatConciergeName,
    getMonthsBetweenDates,
    isInterventionValidated,
} from '../utils/interventionHoursExport';

export default function Concierges(props) {
    const userContext = React.useContext(UserContext)

    const [item, setItem] = React.useState({})
    const [showExportDialog, setShowExportDialog] = React.useState(false);
    const [exportStartDate, setExportStartDate] = React.useState('');
    const [exportEndDate, setExportEndDate] = React.useState('');
    const [exportLoading, setExportLoading] = React.useState(false);
    const [separateValidated, setSeparateValidated] = React.useState(false);

    const exportInterventionsToExcel = async () => {
        try {
            setExportLoading(true);

            if (!exportStartDate || !exportEndDate) {
                userContext.setError({
                    level: 'warning',
                    message: 'Veuillez sélectionner une période (date de début et date de fin)',
                    btn1Text: 'OK',
                    btn2hidden: true
                });
                setExportLoading(false);
                return;
            }

            const startDate = new Date(exportStartDate);
            const endDate = new Date(exportEndDate);
            endDate.setHours(23, 59, 59, 999);

            userContext.setError({
                level: 'info',
                message: `Calcul des heures d'intervention du ${startDate.toLocaleDateString('fr-FR')} au ${endDate.toLocaleDateString('fr-FR')}...`,
                btn1Text: 'Patienter...',
                btn1hidden: true,
                btn2hidden: true
            });

            // Include archived concierges so past hours and equal splits stay correct
            const concierges = userContext.data?.people?.Concierge || [];
            const conciergeIds = new Set(concierges.map(c => c.id));

            const months = getMonthsBetweenDates(startDate, endDate);

            const conciergeDataMap = new Map();
            concierges.forEach(concierge => {
                const monthlyHours = {};
                const monthlyValidatedHours = {};
                const monthlyNonValidatedHours = {};

                months.forEach(month => {
                    const key = `${month.year}-${month.month}`;
                    monthlyHours[key] = 0;
                    monthlyValidatedHours[key] = 0;
                    monthlyNonValidatedHours[key] = 0;
                });

                conciergeDataMap.set(concierge.id, {
                    conciergeName: formatConciergeName(concierge),
                    monthlyHours,
                    monthlyValidatedHours,
                    monthlyNonValidatedHours,
                });
            });

            const clients = userContext.data?.people?.Client || [];
            clients.forEach(client => {
                if (!Array.isArray(client.properties)) return;

                client.properties.forEach(property => {
                    if (!Array.isArray(property.interventions)) return;

                    property.interventions.forEach(intervention => {
                        if (intervention.cancelled) return;

                        const interventionDate = new Date(intervention.date?.dateStart);
                        if (Number.isNaN(interventionDate.getTime())) return;
                        if (interventionDate < startDate || interventionDate > endDate) return;

                        const key = `${interventionDate.getFullYear()}-${interventionDate.getMonth() + 1}`;
                        if (!months.some(m => `${m.year}-${m.month}` === key)) return;

                        const assignedConcierges = (intervention.people || []).filter(person => conciergeIds.has(person.people));
                        if (assignedConcierges.length === 0) return;

                        const totalHours = durationToHours(intervention.date?.duration);
                        if (totalHours <= 0) return;

                        const splitHours = totalHours / assignedConcierges.length;
                        const interventionValidated = isInterventionValidated(intervention);

                        assignedConcierges.forEach(person => {
                            const conciergeEntry = conciergeDataMap.get(person.people);
                            if (!conciergeEntry) return;

                            conciergeEntry.monthlyHours[key] += splitHours;
                            if (interventionValidated) {
                                conciergeEntry.monthlyValidatedHours[key] += splitHours;
                            } else {
                                conciergeEntry.monthlyNonValidatedHours[key] += splitHours;
                            }
                        });
                    });
                });
            });

            const conciergeData = Array.from(conciergeDataMap.values())
                .map(data => ({
                    ...data,
                    totalHours: roundHours(Object.values(data.monthlyHours).reduce((sum, hours) => sum + hours, 0)),
                    totalValidatedHours: roundHours(Object.values(data.monthlyValidatedHours).reduce((sum, hours) => sum + hours, 0)),
                    totalNonValidatedHours: roundHours(Object.values(data.monthlyNonValidatedHours).reduce((sum, hours) => sum + hours, 0))
                }))
                .filter(data => data.totalHours > 0);

            if (conciergeData.length === 0) {
                userContext.setError({
                    level: 'warning',
                    message: 'Aucune intervention trouvée dans la période sélectionnée',
                    btn1Text: 'OK',
                    btn2hidden: true
                });
                setExportLoading(false);
                return;
            }

            let headers;
            if (separateValidated) {
                headers = [
                    'Concierge',
                    ...months.flatMap(m => [`${m.label} (Validées)`, `${m.label} (Non validées)`]),
                    'Total Validées',
                    'Total Non validées',
                    'Total Général'
                ];
            } else {
                headers = ['Concierge', ...months.map(m => m.label), 'Total'];
            }

            const excelData = [headers];

            conciergeData.forEach(data => {
                let row;
                if (separateValidated) {
                    row = [
                        data.conciergeName,
                        ...months.flatMap(m => {
                            const key = `${m.year}-${m.month}`;
                            return [
                                roundHours(data.monthlyValidatedHours[key] || 0),
                                roundHours(data.monthlyNonValidatedHours[key] || 0)
                            ];
                        }),
                        data.totalValidatedHours,
                        data.totalNonValidatedHours,
                        data.totalHours
                    ];
                } else {
                    row = [
                        data.conciergeName,
                        ...months.map(m => {
                            const key = `${m.year}-${m.month}`;
                            return roundHours(data.monthlyHours[key] || 0);
                        }),
                        data.totalHours
                    ];
                }
                excelData.push(row);
            });

            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.aoa_to_sheet(excelData);

            const maxWidths = excelData[0].map((_, colIndex) => {
                return Math.max(
                    ...excelData.map(row => {
                        const cellValue = row[colIndex];
                        return cellValue ? cellValue.toString().length : 10;
                    })
                );
            });

            ws['!cols'] = maxWidths.map(width => ({ wch: Math.min(width + 2, 50) }));

            XLSX.utils.book_append_sheet(wb, ws, 'Interventions');

            const dateRangeStr = `${exportStartDate.replace(/-/g, '')}_${exportEndDate.replace(/-/g, '')}`;
            const filename = `interventions_concierges_${dateRangeStr}.xlsx`;
            XLSX.writeFile(wb, filename);

            setExportLoading(false);

            let successMessage;
            if (separateValidated) {
                const totalValidated = conciergeData.reduce((sum, c) => sum + c.totalValidatedHours, 0);
                const totalNonValidated = conciergeData.reduce((sum, c) => sum + c.totalNonValidatedHours, 0);
                const totalGeneral = conciergeData.reduce((sum, c) => sum + c.totalHours, 0);
                successMessage = `Export terminé avec succès pour ${conciergeData.length} concierges.\n` +
                    `Validées: ${totalValidated.toFixed(2)}h | Non validées: ${totalNonValidated.toFixed(2)}h | Total: ${totalGeneral.toFixed(2)}h`;
            } else {
                const totalHours = conciergeData.reduce((sum, c) => sum + c.totalHours, 0);
                successMessage = `Export terminé avec succès pour ${conciergeData.length} concierges. Total: ${totalHours.toFixed(2)} heures.`;
            }

            userContext.setError({
                level: 'success',
                message: successMessage,
                btn1Text: 'OK',
                btn2hidden: true
            });
        } catch (error) {
            console.error("Error exporting interventions to Excel:", error);
            setExportLoading(false);
            userContext.setError({
                level: 'error',
                message: `Erreur lors de l'export: ${error.message || 'Erreur inconnue'}`,
                btn1Text: 'OK',
                btn2hidden: true
            });
        }
    };

    const sortedConcierges = React.useMemo(() => {
        const list = userContext.data?.people?.Concierge || [];
        return [...list].sort((a, b) => {
            const archiveDiff = Number(!!a.archive) - Number(!!b.archive);
            if (archiveDiff !== 0) return archiveDiff;
            return (a.lastName || '').localeCompare(b.lastName || '', 'fr');
        });
    }, [userContext.data?.people?.Concierge]);

    if (userContext.loading) {
        return (
            <div className={"col border cardsContainer people-directory mx-1 p-2 mt-3 mt-md-0"}>
                <div className={"row justify-content-between align-items-center mb-3"}>
                    <div className={"col-auto"}>
                        <h1>Concierges</h1>
                    </div>
                    <div className={"col-auto d-flex gap-2"}>
                        <button
                            type={"button"}
                            className={'btn btn-outline-success'}
                            disabled
                        >
                            <BsDownload className="me-2" />
                            Extraction des interventions
                        </button>
                        <button type={"button"} className={'btn btn-outline-dark '} onClick={() => {
                            const object = {}
                            object.choices = "Concierge"
                            setItem(object)
                        }}>
                            <h3><BsPlus /></h3>
                        </button>
                    </div>
                </div>

                <table className="table table-hover people-table">
                    <thead>
                        <tr>
                            <th>Nom</th>
                            <th>Prénom</th>
                            <th>Téléphone</th>
                            <th>Email</th>
                            <th>Contract</th>
                            <th>Ville de résidence</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td colSpan={6}>
                                <Spinner strokeWidth={9} width={40} color={"#003952"} />
                            </td>
                        </tr>
                    </tbody>
                </table>
                <Overlay item={item}
                    close={() => {
                        setItem(false)
                    }}
                    edit={(value) => {
                        setItem(value)
                    }} />
            </div>
        )
    }

    return (
        <div className={"col border cardsContainer people-directory mx-1 p-2 mt-3 mt-md-0"}>
            <div className={"row justify-content-between align-items-center mb-3"}>
                <div className={"col-auto"}>
                    <h1>Concierges</h1>
                </div>
                <div className={"col-auto d-flex gap-2"}>
                    <button
                        type={"button"}
                        className={'btn btn-outline-success'}
                        onClick={() => setShowExportDialog(!showExportDialog)}
                    >
                        <BsDownload className="me-2" />
                        Extraction des interventions {showExportDialog ? '▲' : '▼'}
                    </button>
                    <button type={"button"} className={'btn btn-outline-dark '} onClick={() => {
                        const object = {}
                        object.choices = "Concierge"
                        setItem(object)
                    }}>
                        <h3><BsPlus /></h3>
                    </button>
                </div>
            </div>

            {showExportDialog && (
                <div className="filters-panel mt-3 p-3 border rounded bg-light mb-3">
                    <div className="row align-items-end">
                        <div className="col-12 mb-3">
                            <h5>Extraction des heures d'intervention par concierge</h5>
                            <p className="small text-muted">
                                Sélectionnez une période pour calculer le cumul d'heures d'intervention par mois pour chaque concierge.
                                Si plusieurs concierges sont affectés à une intervention, la durée est répartie équitablement.
                                Les concierges archivés restent inclus dans l'export.
                            </p>
                        </div>
                        <div className="col-12 mb-3">
                            <div className="form-check">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="separateValidatedCheckboxConcierges"
                                    checked={separateValidated}
                                    onChange={(e) => setSeparateValidated(e.target.checked)}
                                />
                                <label className="form-check-label" htmlFor="separateValidatedCheckboxConcierges">
                                    Séparer interventions validées et non validées
                                    <small className="d-block text-muted">
                                        Une intervention est validée si tous les intervenants ont un rapport et une date de fin.
                                    </small>
                                </label>
                            </div>
                        </div>
                        <div className="col-md-4 mb-3">
                            <label className="form-label">Date de début <span className="text-danger">*</span></label>
                            <input
                                type="date"
                                className="form-control"
                                value={exportStartDate}
                                onChange={(e) => setExportStartDate(e.target.value)}
                                required
                            />
                        </div>
                        <div className="col-md-4 mb-3">
                            <label className="form-label">Date de fin <span className="text-danger">*</span></label>
                            <input
                                type="date"
                                className="form-control"
                                value={exportEndDate}
                                onChange={(e) => setExportEndDate(e.target.value)}
                                required
                            />
                        </div>
                        <div className="col-md-4 mb-3">
                            <button
                                className="btn btn-success w-100"
                                onClick={exportInterventionsToExcel}
                                disabled={exportLoading || !exportStartDate || !exportEndDate}
                            >
                                {exportLoading ? (
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                ) : (
                                    <BsDownload className="me-2" />
                                )}
                                Exporter en Excel
                            </button>
                        </div>
                    </div>

                    <HoursMatrixExport
                        clients={userContext.data?.people?.Client || []}
                        concierges={userContext.data?.people?.Concierge || []}
                        setError={userContext.setError}
                    />
                </div>
            )}

            <div>
                <table className="table table-hover people-table">
                    <thead>
                        <tr>
                            <th>Nom</th>
                            <th>Prénom</th>
                            <th>Téléphone</th>
                            <th>Email</th>
                            <th>Contract</th>
                            <th>Ville de résidence</th>
                        </tr>
                    </thead>
                    <tbody>
                        {
                            sortedConcierges.length > 0 ?
                                sortedConcierges.map((concierge) => {
                                    return (
                                        <tr
                                            key={concierge.id || concierge.email}
                                            className={concierge.archive ? 'table-secondary text-muted' : undefined}
                                            onClick={() => {
                                                const object = concierge
                                                object.choices = "ConciergeModify"
                                                setItem(object)
                                            }}
                                        >
                                            <td>
                                                {concierge.lastName}
                                                {concierge.archive ? (
                                                    <span className="badge bg-secondary ms-2">Archivé</span>
                                                ) : null}
                                            </td>
                                            <td>{concierge.name}</td>
                                            <td>{concierge.phone}</td>
                                            <td>{concierge.email}</td>
                                            <td>{concierge.contract?.type}</td>
                                            <td>{concierge.address?.city}</td>
                                        </tr>
                                    )
                                })
                                :
                                <tr>
                                    <td colSpan="6">Aucun concierge à afficher.</td>
                                </tr>
                        }
                    </tbody>
                </table>
            </div>
            <Overlay item={item}
                close={() => {
                    setItem(false)
                }}
                edit={(value) => {
                    setItem(value)
                }} />
        </div>
    )
}
